import {
  TWITTER_CONFIG,
  FACEBOOK_CONFIG,
  THREADS_CONFIG,
  INSTAGRAM_CONFIG,
  YOUTUBE_CONFIG,
  TIKTOK_CONFIG,
  LINKEDIN_CONFIG,
  SNAPCHAT_CONFIG,
  generateCodeVerifier,
  generateCodeChallenge,
} from './oauthUtils';

/**
 * Connecting a social channel, browser side.
 *
 *   startChannelAuth(platform, accountId)
 *     opens the platform's sign-in in a popup. Nothing is saved yet: the
 *     sign-in's state (and PKCE verifier) wait in sessionStorage, which the
 *     popup inherits.
 *   /auth/<platform>/callback  (components/auth/ChannelCallback)
 *     trades the code with the API for a TICKET plus the channels that sign-in
 *     can post to, posts CHANNEL_CANDIDATES to the window that opened it, and
 *     closes.
 *   useChannelConnect  (components/reach/ChannelPicker)
 *     receives the candidates, lets the person pick, and connects them with
 *     one mutation. Only then do rows exist.
 */

export const CHANNEL_CANDIDATES = 'CHANNEL_CANDIDATES';
export const CHANNEL_CONNECT_FAILED = 'CHANNEL_CONNECT_FAILED';

/** A channel a sign-in can post to, as the API describes it (no tokens). */
export interface ChannelCandidate {
  platform: string;
  profileId: string;
  name?: string | null;
  username?: string | null;
  link?: string | null;
  /** Already connected and active for this account. */
  connected: boolean;
  /** Has a row (active or disconnected) for this account. */
  known: boolean;
}

export interface ChannelExchange {
  platform: string;
  ticket: string;
  expiresAt: string;
  channels: ChannelCandidate[];
}

/** What a started sign-in leaves for its callback page. */
export interface ChannelConnectSession {
  platform: string;
  accountId: string;
  state: string;
  codeVerifier?: string;
  startedAt: number;
}

const sessionKey = (platform: string) => `channel_connect:${platform}`;
/** Where a finished sign-in also leaves its result, for the page that opens next. */
export const PENDING_RESULT_KEY = 'channel_connect:result';

export const normalizePlatform = (p: string) => {
  const v = (p || '').toLowerCase();
  return v === 'twitter' ? 'x' : v;
};

/** The callback path the API server expects for this platform. */
export const callbackPlatform = (p: string) =>
  normalizePlatform(p) === 'x' ? 'twitter' : normalizePlatform(p);

export function readConnectSession(platform: string): ChannelConnectSession | null {
  try {
    const raw = sessionStorage.getItem(sessionKey(callbackPlatform(platform)));
    return raw ? (JSON.parse(raw) as ChannelConnectSession) : null;
  } catch {
    return null;
  }
}

export function clearConnectSession(platform: string) {
  try {
    sessionStorage.removeItem(sessionKey(callbackPlatform(platform)));
  } catch {
    /* storage may be unavailable */
  }
}

export function stashPendingResult(result: ChannelExchange) {
  try {
    localStorage.setItem(PENDING_RESULT_KEY, JSON.stringify(result));
  } catch {
    /* storage may be unavailable */
  }
}

/** Take (and clear) a result left by a popup whose opener was gone. */
export function takePendingResult(): ChannelExchange | null {
  try {
    const raw = localStorage.getItem(PENDING_RESULT_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_RESULT_KEY);
    const r = JSON.parse(raw) as ChannelExchange;
    return r.ticket && r.expiresAt && Date.parse(r.expiresAt) > Date.now() ? r : null;
  } catch {
    return null;
  }
}

const randomState = () => {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
};

/** Build the sign-in URL (and PKCE verifier where the platform needs one). */
async function authUrl(
  platform: string,
  state: string
): Promise<{ url: string; codeVerifier?: string }> {
  switch (normalizePlatform(platform)) {
    case 'x': {
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const q = new URLSearchParams({
        response_type: 'code',
        client_id: TWITTER_CONFIG.clientId || '',
        redirect_uri: TWITTER_CONFIG.redirectUri,
        scope: TWITTER_CONFIG.scope,
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      });
      return { url: `${TWITTER_CONFIG.authUrl}?${q}`, codeVerifier };
    }
    case 'facebook': {
      // One Meta sign-in covers every Page the person manages and the Instagram
      // accounts linked to them. With a Facebook Login for Business
      // configuration id the dialog uses that configuration's permissions.
      const q = new URLSearchParams({
        client_id: FACEBOOK_CONFIG.clientId || '',
        redirect_uri: FACEBOOK_CONFIG.redirectUri,
        response_type: 'code',
        state,
      });
      if (FACEBOOK_CONFIG.configId) q.set('config_id', FACEBOOK_CONFIG.configId);
      else q.set('scope', FACEBOOK_CONFIG.scope);
      return { url: `${FACEBOOK_CONFIG.authUrl}?${q}` };
    }
    case 'threads':
      return {
        url: `${THREADS_CONFIG.authUrl}?${new URLSearchParams({
          client_id: THREADS_CONFIG.clientId || '',
          redirect_uri: THREADS_CONFIG.redirectUri,
          scope: THREADS_CONFIG.scope,
          response_type: 'code',
          state,
        })}`,
      };
    case 'instagram':
      return {
        url: `${INSTAGRAM_CONFIG.authUrl}?${new URLSearchParams({
          client_id: INSTAGRAM_CONFIG.clientId || '',
          redirect_uri: INSTAGRAM_CONFIG.redirectUri,
          scope: INSTAGRAM_CONFIG.scope,
          response_type: 'code',
          state,
        })}`,
      };
    case 'youtube':
      return {
        url: `${YOUTUBE_CONFIG.authUrl}?${new URLSearchParams({
          client_id: YOUTUBE_CONFIG.clientId || '',
          redirect_uri: YOUTUBE_CONFIG.redirectUri,
          scope: YOUTUBE_CONFIG.scope,
          response_type: 'code',
          state,
          access_type: 'offline',
          // The account with the channel often isn't the one the browser is on.
          prompt: 'select_account consent',
        })}`,
      };
    case 'tiktok':
      return {
        url: `${TIKTOK_CONFIG.authUrl}?${new URLSearchParams({
          client_key: TIKTOK_CONFIG.clientKey || '',
          redirect_uri: TIKTOK_CONFIG.redirectUri,
          scope: TIKTOK_CONFIG.scope,
          response_type: 'code',
          state,
        })}`,
      };
    case 'linkedin':
      return {
        url: `${LINKEDIN_CONFIG.authUrl}?${new URLSearchParams({
          response_type: 'code',
          client_id: LINKEDIN_CONFIG.clientId || '',
          redirect_uri: LINKEDIN_CONFIG.redirectUri,
          scope: LINKEDIN_CONFIG.scope,
          state,
        })}`,
      };
    case 'snapchat':
      return {
        url: `${SNAPCHAT_CONFIG.authUrl}?${new URLSearchParams({
          client_id: SNAPCHAT_CONFIG.clientId || '',
          redirect_uri: SNAPCHAT_CONFIG.redirectUri,
          scope: SNAPCHAT_CONFIG.scope,
          response_type: 'code',
          state,
        })}`,
      };
    default:
      throw new Error(`${platform} cannot be connected here yet.`);
  }
}

/**
 * Open the sign-in for a channel of this platform in a popup. Returns the
 * popup (null when the browser blocked it). The result arrives as a
 * CHANNEL_CANDIDATES message on this window.
 */
export async function startChannelAuth(
  platform: string,
  accountId: string
): Promise<Window | null> {
  const state = randomState();
  const { url, codeVerifier } = await authUrl(platform, state);
  const session: ChannelConnectSession = {
    platform: normalizePlatform(platform),
    accountId,
    state,
    codeVerifier,
    startedAt: Date.now(),
  };
  sessionStorage.setItem(sessionKey(callbackPlatform(platform)), JSON.stringify(session));
  return window.open(
    url,
    `${normalizePlatform(platform)}_auth`,
    'width=600,height=720,scrollbars=yes,resizable=yes'
  );
}
